import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Mail, Lock, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Crest, TenantThemer } from "../../components/shell";
import { FloodlightLoader } from "../../components/FloodlightLoader";

/**
 * Tenant-branded sponsor login.
 *
 * Two ways to land here:
 *   1. /c/:slug          → the club admin shares this URL with their sponsors
 *   2. {slug}.floodlight.club  → wildcard subdomain (DNS provisioned later);
 *      the App detects subdomain and renders this page with that slug
 *
 * Visual is fully tenant-themed (primary colour gradient, crest, name, tagline).
 * Auth flow is identical to /login — same backend, same JWT cookie.
 */
export default function ClubLogin({ slugOverride }) {
    const params = useParams();
    const slug = slugOverride || params.slug;
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();

    const [tenant, setTenant] = useState(null);
    const [tenantErr, setTenantErr] = useState("");

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    // Load tenant brand
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Public endpoint — no axios `withCredentials` needed
                const base = process.env.REACT_APP_BACKEND_URL;
                const { data } = await axios.get(`${base}/api/public/tenants/by-slug/${slug}`);
                if (!cancelled) setTenant(data);
            } catch (e) {
                if (!cancelled) setTenantErr(e.response?.status === 404 ? "Club not found" : "Couldn't load this club's login page.");
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    const goHome = (user) => {
        const from = location.state?.from?.pathname;
        if (from && from !== `/c/${slug}`) return navigate(from, { replace: true });
        if (user.role === "sponsor") return navigate("/sponsor", { replace: true });
        if (user.role === "club_admin") return navigate("/club", { replace: true });
        if (user.role === "platform_owner") return navigate("/platform", { replace: true });
        navigate("/", { replace: true });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            const u = await login(email, password);
            goHome(u);
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setBusy(false);
        }
    };

    if (tenantErr) {
        return (
            <div className="min-h-screen bg-[var(--sb-page)] text-white flex items-center justify-center p-12" data-testid="club-login-error">
                <div className="border hairline p-10 max-w-md text-center">
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">{slug}.floodlight.club</div>
                    <h1 className="font-sport text-3xl uppercase mb-3">{tenantErr}</h1>
                    <p className="text-sm text-zinc-400">Double-check the link your club sent you. If it's wrong, we'd love to know.</p>
                    <button onClick={() => navigate("/login")} className="mt-6 text-xs uppercase tracking-ultra-wide text-zinc-400 hover:text-white border hairline px-4 py-2.5">
                        Sign in to Floodlight directly
                    </button>
                </div>
            </div>
        );
    }

    if (!tenant) {
        return (
            <div className="min-h-screen bg-[var(--sb-page)] text-white flex items-center justify-center" data-testid="club-login-loading">
                <FloodlightLoader label="Switching the lights on" />
            </div>
        );
    }

    const primary = tenant.brand?.primary || "#F59E0B";
    const fg = tenant.brand?.primary_foreground || "#000000";
    const secondary = tenant.brand?.secondary || primary;

    return (
        <div data-testid="club-login-page" className="min-h-screen relative overflow-hidden bg-[var(--sb-page)] text-white">
            <TenantThemer tenant={tenant} />

            {/* Background — tenant-coloured radial gradients + grain */}
            <div
                aria-hidden
                className="absolute inset-0 z-0"
                style={{
                    background: `
                        radial-gradient(ellipse 60% 40% at 50% 0%, ${primary}26 0%, transparent 55%),
                        radial-gradient(circle 40rem at 90% 100%, ${secondary}1f 0%, transparent 60%),
                        radial-gradient(circle 50rem at 5% 110%, ${primary}14 0%, transparent 60%)
                    `,
                }}
            />
            <div aria-hidden className="absolute inset-0 z-0 editorial-grid opacity-40" />

            <div className="relative z-10 min-h-screen flex flex-col">
                {/* Top strip */}
                <header className="px-6 md:px-12 pt-8 flex items-center justify-between">
                    <div className="flex items-center gap-3" data-testid="club-login-header">
                        <Crest tenant={tenant} size={42} />
                        <div className="leading-tight">
                            <div className="font-display font-bold text-base tracking-tight">{tenant.name}</div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Sponsor sign-in</div>
                        </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 hidden md:block">
                        {tenant.season} · {tenant.league || "Floodlight"}
                    </div>
                </header>

                {/* Center card */}
                <main className="flex-1 flex items-center justify-center px-6 py-16">
                    <div className="w-full max-w-md">
                        {tenant.matchday_page_enabled && (
                            <a
                                href={`/${tenant.slug}/spots`}
                                className="group block border hairline mb-8 p-5 transition-colors hover:border-white/30 bg-[rgba(0,0,0,0.4)] backdrop-blur-sm"
                                style={{ borderColor: `${primary}55` }}
                                data-testid="club-login-spots-cta"
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="text-[10px] uppercase tracking-ultra-wide" style={{ color: primary }}>
                                            Not a sponsor yet?
                                        </div>
                                        <div className="font-sport text-base uppercase mt-1 tracking-tight">
                                            See what's available now
                                        </div>
                                        <div className="text-xs text-zinc-400 mt-1">
                                            Pitch-side boards, app placements, packages. No prices yet — just what's open.
                                        </div>
                                    </div>
                                    <ArrowRight size={18} strokeWidth={2.2} className="shrink-0 group-hover:translate-x-0.5 transition-transform" style={{ color: primary }} />
                                </div>
                            </a>
                        )}

                        <div className="text-center mb-10" data-testid="club-login-hero">
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">Welcome to the {tenant.short_name || tenant.name} sponsor portal</div>
                            <h1 className="font-sport text-4xl md:text-5xl leading-[0.95] uppercase">
                                Sign in to<br />
                                <span style={{ color: primary }}>{tenant.short_name || tenant.name}.</span>
                            </h1>
                            {tenant.brand?.tagline && (
                                <p className="font-editorial italic text-zinc-300 mt-5 text-base md:text-lg">"{tenant.brand.tagline}"</p>
                            )}
                        </div>

                        <form onSubmit={onSubmit} className="space-y-5" data-testid="club-login-form">
                            <div>
                                <label className="text-xs uppercase tracking-ultra-wide text-zinc-500" htmlFor="email">Email</label>
                                <div className="mt-2 relative border hairline focus-within:border-white/30 transition-colors bg-[rgba(0,0,0,0.4)] backdrop-blur-sm">
                                    <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                                    <input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@yourbusiness.co.uk"
                                        className="w-full bg-transparent pl-11 pr-4 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none"
                                        data-testid="club-login-email" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-ultra-wide text-zinc-500" htmlFor="password">Password</label>
                                <div className="mt-2 relative border hairline focus-within:border-white/30 transition-colors bg-[rgba(0,0,0,0.4)] backdrop-blur-sm">
                                    <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                                    <input id="password" type={showPw ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="w-full bg-transparent pl-11 pr-11 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none"
                                        data-testid="club-login-password" />
                                    <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white p-1"
                                        data-testid="club-login-toggle-password" aria-label={showPw ? "Hide password" : "Show password"}>
                                        {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                </div>
                            </div>

                            {err && (
                                <div data-testid="club-login-error-msg" className="border border-rose-500/40 text-rose-300 text-xs px-3 py-2.5">{err}</div>
                            )}

                            <button
                                type="submit"
                                disabled={busy}
                                className="w-full inline-flex items-center justify-center gap-2 font-semibold tracking-tight px-6 py-3.5 transition-all disabled:opacity-50 group"
                                style={{ background: primary, color: fg }}
                                data-testid="club-login-submit"
                            >
                                {busy ? "Signing in…" : <>Sign in to {tenant.short_name || tenant.name} <ArrowRight size={16} strokeWidth={2.4} className="group-hover:translate-x-0.5 transition-transform" /></>}
                            </button>
                        </form>

                        <div className="mt-8 text-[11px] text-zinc-500 text-center">
                            Need help? <a href={`mailto:hello@floodlight.club?subject=Sponsor%20login%20help%20-%20${tenant.name}`} className="text-zinc-300 hover:text-white underline underline-offset-2">Contact the {tenant.short_name || tenant.name} commercial team</a>
                        </div>
                    </div>
                </main>

                {/* Footer */}
                <footer className="px-6 md:px-12 pb-8 flex items-center justify-between text-[11px] text-zinc-600">
                    <span className="font-editorial italic">{tenant.name} sponsor portal</span>
                    <span>powered by <span className="text-zinc-400">FLOODLIGHT</span> · © {new Date().getFullYear()} Soubor Applications Ltd.</span>
                </footer>
            </div>
        </div>
    );
}
